import { Node } from 'prosemirror-model';
import { Command, TextSelection } from 'prosemirror-state';
import { findParentNodeOfType, setTextSelection } from 'prosemirror-utils';

import { isInToggleListHeading } from '../../utils';

export const handleBackspaceInHeading: Command = (state, dispatch) => {
  if (!isInToggleListHeading(state)) {
    return false;
  }

  // if cursor is not at the start of the toggle_heading, do nothing
  const isCursorAtStartOfHeading = (state.selection as TextSelection).$from.parentOffset === 0;

  if (!isCursorAtStartOfHeading) {
    // If the toggle_block_heading has content, do nothing
    return false;
  }

  const listItem = findParentNodeOfType(state.schema.nodes.toggle_list_item)(state.selection);
  const list = findParentNodeOfType(state.schema.nodes.toggle_list)(state.selection);
  const toggleHeadingFragment = listItem?.node.firstChild?.content;
  const toggleContentFragment = listItem?.node.lastChild?.content;

  if (!listItem || !list || !toggleHeadingFragment || !toggleContentFragment) {
    return false;
  }

  const listFragment = list.node.content;
  const isSingleToggleItemPresent =
    listFragment.childCount === 1 && listFragment.firstChild?.eq(listItem.node);

  const tr = state.tr;

  const isFirstChild = listFragment.firstChild?.eq(listItem.node);
  const isLastChild = listFragment.lastChild?.eq(listItem.node);
  const isEmptyToggleListItem = listItem.node.nodeSize === 10;

  if (isFirstChild) {
    // If the toggle item is the first child of the list, we can delete the entire list node
    // and insert the toggle heading and content fragments in its place.
    if (isSingleToggleItemPresent) {
      // If the list only contains one toggle item, we can delete the entire list node.
      tr.delete(list.pos, list.pos + list.node.nodeSize);
    } else {
      // If there are other items in the list, we can delete just the toggle item.
      tr.delete(listItem.pos, listItem.pos + listItem.node.nodeSize);
    }

    // We need to insert the toggle heading and content fragments in the list node's position.
    if (!isEmptyToggleListItem) {
      tr.insert(list.pos, toggleContentFragment).insert(list.pos, toggleHeadingFragment);
    }
  } else if (isLastChild) {
    // If the toggle item is the last child of the list, we can insert the toggle heading
    // and content fragments at the end of the list node.
    const endOfListPos = list.pos + list.node.nodeSize;

    // We need to insert the toggle heading and content fragments at the end of the list node.
    if (!isEmptyToggleListItem) {
      tr.insert(endOfListPos, toggleContentFragment).insert(endOfListPos, toggleHeadingFragment);
    }
    // We can delete the entire toggle item.
    tr.delete(listItem.pos, listItem.pos + listItem.node.nodeSize);
  } else {
    // If the toggle item is neither the first nor the last child of the list,
    // we can insert the toggle heading and content fragments in the toggle item's position.
    if (!isEmptyToggleListItem) {
      tr.insert(listItem.pos, toggleContentFragment).insert(listItem.pos, toggleHeadingFragment);
    }
    // We need to delete the toggle item.
    const newStartPos = tr.mapping.map(listItem.pos);
    const newEndPos = tr.mapping.map(listItem.pos + listItem.node.nodeSize);
    tr.delete(newStartPos, newEndPos);
  }

  if (dispatch) {
    dispatch(tr);
  }

  return true;
};

export const handleEnterInHeading: Command = (state, dispatch) => {
  if (!isInToggleListHeading(state)) {
    return false;
  }

  const listItemNode = findParentNodeOfType(state.schema.nodes.toggle_list_item)(state.selection);
  if (listItemNode?.pos && listItemNode?.node.nodeSize) {
    const insertPos = listItemNode?.pos + listItemNode?.node.nodeSize;
    const node = state.schema.nodes.toggle_list_item.createAndFill();
    if (!node) return false;

    const tr = state.tr.insert(insertPos, node);

    dispatch?.(setTextSelection(insertPos + 2)(tr));

    return true;
  }

  return false;
};
